import type { StyledProps } from '@lokalise/ui/styled';
import styled, { css } from '@lokalise/ui/styled';
import { color, excludeProps } from '@lokalise/ui/theme';

type NamedColor = Parameters<typeof color>[0];
export type Color = NamedColor | 'currentColor' | 'inherit';

type SvgProps = {
    color?: Color;
    fill?: Color;
    stroke?: Color;
    size?: string;
};

const commonConfig = { shouldForwardProp: excludeProps(['fill', 'color', 'stroke']) };

const reservedKeywords = ['currentColor', 'inherit'];

const colorTypesDefaults = {
    color: 'inherit',
    fill: 'currentColor',
    stroke: undefined,
};

const colorHelper = (
    colorType: keyof typeof colorTypesDefaults,
    inputColor: Color | undefined,
    props: StyledProps<unknown>,
) =>
    inputColor && !reservedKeywords.includes(inputColor)
        ? color(inputColor as NamedColor)(props)
        : colorTypesDefaults[colorType];

const commonStyles = css<SvgProps>`
    pointer-events: all;
    box-sizing: border-box;
    color: ${({ color: svgColor, ...props }) => colorHelper('color', svgColor, props)};
    fill: ${({ fill, ...props }) => colorHelper('fill', fill, props)};
    stroke: ${({ stroke, ...props }) => colorHelper('stroke', stroke, props)};
    font-size: ${(props) => props.size};
`;

export const SvgRoot = styled.svg.withConfig<SvgProps>(commonConfig)`
    ${commonStyles};
    width: 1em;
    height: 1em;
`;

export const Path = styled.path.withConfig<SvgProps>(commonConfig)`
    ${commonStyles};
`;

export const Rect = styled.rect.withConfig<SvgProps>(commonConfig)`
    ${commonStyles};
`;

export const G = styled.g.withConfig<SvgProps>(commonConfig)`
    ${commonStyles};
`;

export const Circle = styled.circle.withConfig<SvgProps>(commonConfig)`
    ${commonStyles};
`;

export const LinearGradient = styled.linearGradient.withConfig<SvgProps>(commonConfig)`
    ${commonStyles};
`;

export const Defs = styled.defs.withConfig<SvgProps>(commonConfig)`
    ${commonStyles};
`;

export const Stop = styled.stop.withConfig<SvgProps>(commonConfig)`
    ${commonStyles};
`;

export const ClipPath = styled.clipPath.withConfig<SvgProps>(commonConfig)`
    ${commonStyles};
`;
